import React, { createContext, useContext, useEffect } from 'react';
import { useAuthStore } from '../services/auth';
import type { User } from '@supabase/supabase-js';
import { setupRealtimeSync } from '../services/sync';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    user, 
    loading: isLoading, 
    signOut, 
    initializeAuth, 
    showAuthModal,
    setShowAuthModal,
    setUser,
    setSession
  } = useAuthStore();

  useEffect(() => {
    // Initialize auth state
    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Setup real-time sync when user is authenticated
        const cleanup = setupRealtimeSync();
        return () => {
          cleanup?.();
        };
      }
    });

    return () => subscription.unsubscribe();
  }, [initializeAuth, setUser, setSession]);

  const value = {
    user,
    isLoading,
    signOut,
    showAuthModal,
    setShowAuthModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
